import { ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { api } from '../../services/api';
import { fmt } from '../../lib/format';
import type { ChecklistGroup } from '../../contracts';
import { InfoNotice } from '../ui/States';
import { useCanSee } from '../../contexts/AuthContext';
import { FORMAT_LABEL, KIND_LABEL, dayLabel, eventDates, torontoToday } from '../../lib/events';
import { areaVisible, shapeOf } from '../../contracts';
import { ShowAllTabs, useEventContent } from './pageContext';
import type { Dashboard, PanelProps } from './types';
import { STAGES, STAGE_LABEL, Stage, panelFor, stageOf } from './stages';
import Placeholder from './Placeholder';
import { extrasFor } from './plugins';
import { AddBar } from '../ui/AddLink';

const tabClass = (on: boolean) =>
  `py-3 text-[15px] leading-5 whitespace-nowrap ${
    on ? 'text-ink font-semibold shadow-[inset_0_-2px_0_#2A2420]' : 'text-ink-2 hover:text-ink'
  }`;

/** The numbers beside tab names: open follow-ups, people on the list, late timeline steps. Loaded for the Overview. */
function useTabCounts(eventId: string) {
  const dashboard = useQuery<Dashboard>(
    ['event-dashboard', eventId],
    async () => (await api.get(`/events/${eventId}/dashboard`)).data.data,
    { refetchInterval: 60000 },
  );
  const checklist = useQuery<ChecklistGroup[]>(
    ['checklist', 'event', eventId],
    async () => (await api.get('/checklists', { params: { subjectType: 'event', subjectId: eventId } })).data.data,
  );
  const late = (checklist.data ?? []).reduce((n, g) => n + (g.overdue ?? 0), 0);
  const d = dashboard.data;
  return {
    followups: d && d.openFollowups ? fmt(d.openFollowups) : null,
    people: d && d.invitees ? fmt(d.invitees) : null,
    timeline: late ? `${fmt(late)} late` : null,
  } as Record<string, string | null>;
}

/** The event page: header with countdown, Before · During · After, and each stage's tabs. */
export default function EventShell({
  aside,
  banner,
  ...props
}: Omit<PanelProps, 'go' | 'day'> & { aside?: ReactNode; banner?: ReactNode }) {
  const see = useCanSee();
  const e = props.detail.event;
  const now = stageOf(e, torontoToday());
  const [picked, setPicked] = useState<Stage>(now.stage);
  const [tabs, setTabs] = useState<Record<Stage, string>>({ before: 'overview', during: 'day', after: 'attendance' });
  const [day, setDay] = useState(now.day);
  const [showAllPicked, setShowAll] = useState(false);
  const [added, setAdded] = useState<string[]>([]);
  const content = useEventContent(e.id);
  const light = shapeOf(e.kind) === 'light';
  const showAll = showAllPicked && !light;
  const over = now.stage === 'after';
  const allowed = (s: Stage) => STAGES[s].filter((t) => !t.feature || see(t.feature));
  const visible = (s: Stage) =>
    allowed(s).filter(
      (t) =>
        added.includes(`${s}.${t.key}`) || areaVisible(e.kind, `${s}.${t.key}`, content.data, { ended: over, showAll }),
    );
  const order: Stage[] = ['before', 'during', 'after'];
  const stages = order.filter((s) => visible(s).length);
  const stage = stages.includes(picked) ? picked : (stages[0] ?? picked);
  const hidden = order.reduce((n, s) => n + allowed(s).length - visible(s).length, 0);
  const addable = order.flatMap((s) =>
    allowed(s)
      .filter((t) => !visible(s).includes(t))
      .map((t) => ({ s, t })),
  );
  const go = (s: Stage, t: string) => {
    setPicked(s);
    setTabs((x) => ({ ...x, [s]: t }));
  };
  const tab = visible(stage).find((t) => t.key === tabs[stage]) ?? visible(stage)[0];
  const Panel = tab ? panelFor(stage, tab) : undefined;
  const extras = stage === 'during' || !tab ? [] : extrasFor(`${stage}.${tab.key}`);
  const dates = eventDates(e);
  const counts = useTabCounts(e.id);
  return (
    <ShowAllTabs.Provider value={showAll}>
      <div className="pt-2 max-md:pt-0">
        <span className="text-[15px] leading-5">
          <Link className="text-accent hover:text-ink" to="/events">
            All events
          </Link>
          {props.detail.parent ? (
            <span className="text-ink-2">
              {' '}
              · part of{' '}
              <Link className="text-accent hover:text-ink" to={`/events/${props.detail.parent.id}`}>
                {props.detail.parent.title}
              </Link>
            </span>
          ) : null}
        </span>
        <div className="flex flex-wrap items-end justify-between gap-x-8 gap-y-4 mt-2">
          <div className="flex-[1_1_560px] min-w-0">
            <h1 className="font-serif font-semibold text-display text-ink max-md:text-[26px] max-md:leading-8">
              {e.title}
            </h1>
            <p className="mt-[6px] text-[15px] leading-[22px] text-ink-2">
              {[
                dates,
                [e.venue, e.city].filter(Boolean).join(', '),
                FORMAT_LABEL[e.mode] ?? null,
                KIND_LABEL[e.kind] ?? e.kind,
              ]
                .filter(Boolean)
                .join(' · ')}
              {stage === 'before' && tab?.key === 'overview' && props.detail.subEvents?.length ? (
                <>
                  {' · Related: '}
                  {props.detail.subEvents.map((x, i) => (
                    <span key={x.id}>
                      {i ? ', ' : ''}
                      <Link className="text-accent hover:text-ink" to={`/events/${x.id}`}>
                        {x.title}
                      </Link>
                      {x.days[0] ? `, ${dayLabel(x.days[0].date)}` : ''}
                    </span>
                  ))}
                </>
              ) : null}
            </p>
            {banner ? <div className="mt-3">{banner}</div> : null}
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-[15px] leading-5 font-semibold text-ink">{now.countdown}</span>
            {stages.length > 1 && !light ? (
              <div
                className="inline-flex rounded-sm border border-line-strong overflow-hidden"
                role="group"
                aria-label="Stage"
              >
                {stages.map((s, i) => (
                  <button
                    key={s}
                    type="button"
                    aria-pressed={stage === s}
                    aria-current={s === now.stage ? 'date' : undefined}
                    title={s === now.stage ? 'Where the event is today' : undefined}
                    onClick={() => setPicked(s)}
                    className={`min-h-[40px] px-3 py-2 text-[15px] leading-5 ${i ? 'border-l border-line-strong' : ''} ${
                      stage === s ? 'bg-ink text-paper font-semibold' : 'text-ink hover:bg-selected'
                    }`}
                  >
                    {STAGE_LABEL[s]}
                  </button>
                ))}
              </div>
            ) : null}
            {aside ? <div className="max-md:hidden">{aside}</div> : null}
          </div>
        </div>
      </div>
      <nav
        aria-label="Event sections"
        className="flex items-center gap-6 mt-6 mb-10 border-b border-line overflow-x-auto max-md:-mx-4 max-md:px-4 max-md:gap-5 max-md:mt-4 max-md:mb-6"
      >
        {stage === 'during'
          ? e.days.map((d, i) => (
              <button
                key={d.date}
                onClick={() => setDay(d.date)}
                aria-current={day === d.date ? 'page' : undefined}
                className={tabClass(day === d.date)}
              >
                Day {i + 1} · {dayLabel(d.date)}
                {d.label ? ` · ${d.label}` : ''}
              </button>
            ))
          : (light
              ? stages.flatMap((st) => visible(st).map((t) => ({ st, t })))
              : visible(stage).map((t) => ({ st: stage, t }))
            ).map(({ st, t }) => (
              <button
                key={`${st}.${t.key}`}
                onClick={() => go(st, t.key)}
                aria-current={st === stage && t.key === tab?.key ? 'page' : undefined}
                className={tabClass(st === stage && t.key === tab?.key)}
              >
                {t.label}
                {st === 'before' && counts[t.key] ? (
                  <span className="text-ink-3 font-normal ml-1">{counts[t.key]}</span>
                ) : null}
              </button>
            ))}
        {light && addable.length && props.mutable ? (
          <select
            aria-label="Add a tab"
            className="ml-auto min-h-[36px] px-2 py-1 text-[15px] text-accent bg-transparent border-0"
            value=""
            onChange={(ev) => {
              const [st, key] = ev.target.value.split('.') as [Stage, string];
              setAdded((x) => [...x, ev.target.value]);
              go(st, key);
            }}
          >
            <option value="">+ Add…</option>
            {addable.map(({ s: st, t }) => (
              <option key={`${st}.${t.key}`} value={`${st}.${t.key}`}>
                {t.label}
              </option>
            ))}
          </select>
        ) : !light && ((hidden > 0 && props.mutable) || showAll) ? (
          <button
            onClick={() => setShowAll((v) => !v)}
            className="ml-auto py-3 text-[15px] leading-5 whitespace-nowrap text-accent underline underline-offset-[3px] hover:text-ink"
          >
            {showAll ? 'Hide empty tabs' : 'Show empty tabs'}
          </button>
        ) : null}
      </nav>
      <div>
        {!tab && stage !== 'during' ? (
          <InfoNotice>Nothing was recorded for this stage.</InfoNotice>
        ) : Panel ? (
          <Panel {...props} go={go} day={stage === 'during' ? day : undefined} />
        ) : extras.length ? null : (
          <Placeholder title={tab.label} comingWith={tab.comingWith} />
        )}
        {extras.map((Extra, i) => (
          <Extra key={i} eventId={e.id} canEdit={props.mutable} />
        ))}
        <AddBar />
      </div>
    </ShowAllTabs.Provider>
  );
}
